
import type { TableProps as AntTableProps } from 'antd'
import { utils } from 'apprise-core/utils'
import { useT } from 'apprise-frontend-core/intl/language'
import { Label } from 'apprise-ui/label/label'
import { MultiSelectBox, SelectBox } from 'apprise-ui/selectbox/selectbox'
import { TableContext, TableStackContext } from 'apprise-ui/table/context'
import { ColumnProps, defaultRowId, InnerTableProps, TableElement, TableProps } from 'apprise-ui/table/table'
import { Fragment, ReactNode, useContext, useMemo } from 'react'
import { BiSolidLayer } from 'react-icons/bi'

import fastsort from "fast-sort"


export type StackSelector<E extends TableElement> = (_: E) => string | undefined

export type Stack<E extends TableElement> = {

    // stack identifier, typically the name of the column that gets highlighted.
    cols?: string | string[]

    // the stack of an element, often the value of the column.
    stackOf?: StackSelector<E> | StackSelector<E>[]

    // how to present the stack (as a string, it gets translated.)
    name: string | ReactNode

    // the space expected for the name as a dropdown option (set on longest).
    width?: number



}


// the properties of the stack top row

const childrenprop = '__children'
const countprop = '__count'

export type StackedElement = TableElement & Partial<{
    [childrenprop]: TableElement[]
    [countprop]: number

}>


export type StackState = {

    expandedRows: string[]
    selectedStack: Stack<TableElement> | undefined
}



// post-processes configuraton directives and control access to context.

export const useStacks = <E extends TableElement>(props: TableProps<E>) => {

    const { name, rowStacks } = props

    const stackctx = useContext(TableStackContext)

    const stackMode = (!rowStacks || !name) ? 'disabled' : typeof rowStacks === 'function' ? 'single' : props.stackMode === 'dynamic' ? 'dynamic' : 'multiple'

    const stacks: Stack<E>[] = stackMode === 'disabled' ? []
        : stackMode === 'single' ? [{ cols: 'stack', name: 'stack', stackOf: rowStacks as StackSelector<E> }]
            : (rowStacks as Stack<E>[]).map(g => g.cols ? g : { ...g, cols: 'none' })


    const nostack = stacks.find(s => s.cols === 'none')

    const stacksEnabled = name && stackMode !== 'disabled'

    const stackNameOf = <E extends TableElement>(stack: Stack<E>) => utils().arrayOf(stack.cols).join('+')

    const stackMap = utils().index(stacks).by(stackNameOf)

    const selectedStack = stacksEnabled ? stackctx.get().selectedStack ?? nostack : undefined

    const selectedStackId = selectedStack ? stackNameOf(selectedStack) : undefined

    const stackSelectors = selectedStack ? utils().arrayOf(selectedStack.stackOf) as StackSelector<E>[] : undefined

    const selectStack = (stack: Stack<E> | undefined) =>

        stackctx.set(c => {

            c.expandedRows = []
            c.selectedStack = stack as Stack<TableElement> ?? nostack

        })


    const selectCompositeStack = (stacks: string[] | undefined) => {

        if (!stacks?.length)
            return selectStack(nostack)

        const cols = stacks
        const stackOf = stacks.flatMap(s => utils().arrayOf(stackMap[s].stackOf ?? []))

        const composite = { name: 'composite', cols, stackOf }

        selectStack(composite)

    }

    const stackLayoutOf = (layout: ColumnProps<E>[]) => {

        // use the index of colums into the array of stacked cols to have the latter perculate to the start of the layout in the stacking order.
        // but we're indexing positions explicitly with a Map, because indexOf() would returns -1 for unstacked columns. complicating the algo.
        const cols = new Map(utils().arrayOf(selectedStack?.cols).map((col, index) => [col, index]));

        return stackSelectors?.length ? [stackColumnProps, ...layout.sort((c1, c2) => (cols.get(c1.name) ?? Infinity) - (cols.get(c2.name) ?? Infinity))] : layout
    }


    return { stacks, stackMode, selectedStack, selectedStackId, stackMap, stackSelectors, stackNameOf, selectStack, selectCompositeStack, stackLayoutOf }

}

export const useTopstackFilter = () => (row: TableElement) => !row[childrenprop]




export const useStackRows = <E extends TableElement>(props: TableProps<E>) => {

    const stackctx = useContext(TableStackContext)

    const rowid = (row: E) => typeof props.rowId === 'string' ? row[props.rowId] : props.rowId?.(row)

    const clearExpandedRows = () => stackctx.setQuietly(s => s.expandedRows = [])

    const expandedRows = () => stackctx.get().expandedRows

    const expandRow = (row: E) => stackctx.set(s => s.expandedRows = [...s.expandedRows, rowid(row)])

    const collapseRow = (row: E) => stackctx.set(s => s.expandedRows = s.expandedRows.filter(id => id !== rowid(row)))

    const isRowExplanded = (row: E) => stackctx.get().expandedRows.includes(rowid(row))

    return { clearExpandedRows, expandedRows, expandRow, collapseRow, isRowExplanded }
}


const countChildren = (rows: TableElement | TableElement[] | undefined) => rows == undefined ? 1 : Array.isArray(rows) ? rows.reduce((acc, ee) => (ee[countprop] ?? 1) + acc, 0) ?? 1 : countChildren(rows[childrenprop])





// extra column with stack counts.
export const stackColumnProps = {
    name: 'stack-col',
    classnameFor: (_, row) => `stack-col-${countChildren(row)}`,
    width: 80,
    sort: utils().compareNumbersOf(countChildren),
    title: <BiSolidLayer style={{ marginTop: 6, color: 'dodgerblue' }} />,
    text: countChildren,
    render: (row: StackedElement) => {

        const count = countChildren(row)

        return <span style={{ fontWeight: 600, fontSize: 11 }}>{count > 1 ? count : undefined}

        </span>
    }
} as ColumnProps






// used by the table to group and style.
export const useStackedData = <E extends TableElement>(props: InnerTableProps<E>) => {

    const { data = [], rowStacks } = props

    const { stackSelectors, selectedStack } = useStacks(props)

    const { expandedRows, expandRow, collapseRow, isRowExplanded } = useStackRows(props)


    const ctx = useContext(TableContext)


    // we may need to sort by stack col, as counts are aavailable only now.

    const stackSortSpec = props.name && stackSelectors?.length ? ctx.get()[props.name]?.sort.find(spec => spec.key === 'stack-col') : undefined

    const sortByCount = (rows: E[]) => stackSortSpec?.mode === 'asc' ? fastsort(rows).asc(e => e[countprop]) : fastsort(rows).desc(e => e[countprop])

    const stackrec = (selectors: StackSelector<E>[] | undefined, data: E[]) => {

        if (!selectors?.length)
            return data

        const [selector, ...otherSelectors] = selectors

        const stacked: E[] = Object.values(

            utils().index(data).byGroup(selector)

        )
            .reduce((acc, group) => {

                // just one row: clear up any children it may have had in previous groupings.
                if (group.length === 1)
                    return [...acc, { ...group[0], [childrenprop]: undefined, [countprop]: 1 }]

                // apply other remaining selectors recursively, and get plain rows or stacks back.
                const children = stackrec(otherSelectors, group)

                // order children as per specs, default is count descending.
                const sortedChildren = sortByCount(children)

                // look at the first child.
                const [firstChild, ...otherChildren] = sortedChildren


                const toprowid = props.rowId && props.rowId !== defaultRowId ? typeof props.rowId === 'string' ? `${firstChild[props.rowId]}-toprow` : `${props.rowId(firstChild)}-toprow` : firstChild.id ? `${firstChild.id}-toprow` : props.rowIdGenerator?.()

                if (!toprowid)
                    throw new Error("can't generate new row id for stacking, have you defined a rowIdGenerator()?")

                // create a virtual clone to use as topstack.
                const firstChildClone = { ...utils().deepclone(firstChild), id: toprowid }


                // if it's a plain row or one stack of many, stack all children.
                if (!firstChild[childrenprop] || otherChildren.length)
                    return [...acc, { ...firstChildClone, [childrenprop]: sortedChildren, [countprop]: countChildren(sortedChildren) }]

                // if it's the only stack, unwrap its own children.
                const top = { ...firstChildClone, [childrenprop]: firstChild[childrenprop], [countprop]: countChildren(firstChild[childrenprop]) }

                return [...acc, top]


            }, [])


        return sortByCount(stacked)
    }

    // eslint-disable-next-line
    const stackedData = useMemo(() => stackrec(stackSelectors, data), [data, rowStacks, selectedStack]) // group data by rowGroup function, if specified, otherwise use data as is.

    const stackedRowClassname = (row: E & StackedElement, i: number) => stackSelectors?.length && row[childrenprop] ? isRowExplanded(row) ? `topstack topstack-expanded topstack-expanded-${selectedStack}-${i} row-expanded-${selectedStack}` : `topstack-collapsed-${selectedStack}-${i} row-collapsed-${selectedStack} topstack topstack-collapsed` : ''

    const stackedTitleClassname = (id: string | undefined) => utils().arrayOf(selectedStack?.cols)?.includes(id) ? `col-stacked` : ''

    const antExpendableConfig: AntTableProps<E>['expandable'] = {

        childrenColumnName: childrenprop,

        expandedRowKeys: expandedRows(),

        indentSize: 20,

        onExpand: (value, row) => {

            if (value)
                expandRow(row)
            else
                collapseRow(row)
        }

    }

    return { stackedData, antExpendableConfig, stackedRowClassname, stackedTitleClassname }

}

// used by the table bar to place the dropdown selector.

export const useStackSelector = <E extends TableElement>(props: TableProps<E>) => {

    const { stackMode } = useStacks(props)

    const StackSelector = stackMode === 'dynamic' ? <DynamicStackSelector {...props} /> : stackMode === 'multiple' ? <StaticStackSelector {...props} /> : null


    return { StackSelector }

}

const StaticStackSelector = <E extends TableElement>(props: TableProps<E>) => {

    const t = useT()

    const { stacks, selectStack, stackNameOf, selectedStack } = useStacks(props)

    const width = Math.max(200, ...stacks.map(g => g.width ?? 0))

    return <SelectBox noReadonly noClear noSearch minWidth={width}
        options={stacks}
        keyOf={stackNameOf}
        render={stack => <Label icon={<BiSolidLayer />} title={(typeof stack.name) === 'string' ? t(stack.name) : stack.name} />}
        onChange={selectStack}>

        {selectedStack}
    </SelectBox>

}


const DynamicStackSelector = <E extends TableElement>(props: TableProps<E>) => {

    const t = useT()

    const { stacks, selectedStack, selectCompositeStack, stackMap } = useStacks(props)

    const width = Math.max(230, ...stacks.map(g => g.width ?? 0))

    const selected = selectedStack?.cols ? utils().arrayOf(selectedStack?.cols) : undefined

    const options = Object.keys(stackMap).filter(o => o !== 'none')


    return <div className='dynamic-stack-selector'>
        <div className='selector-prefix'>
            <Label icon={<BiSolidLayer />} title={t("ui.table.stackdynamic_prefix")} />
        </div>  
        <MultiSelectBox placeholder={t('ui.table.stackdynamic_placeholder')} noReadonly noSearch minWidth={width}
            options={options}
            render={o => <Label noIcon title={<>
                <span>{(typeof stackMap[o]?.name) === 'string' ? t(stackMap[o].name) : stackMap[o]?.name}</span>
                {/* {selected && selected.indexOf(o) > -1 ? 
                    <span className='selector-option-index'>[{selected.indexOf(o)+1}]</span>
                : null} */}
            </>} 
        />}
            onChange={selectCompositeStack}>

            {selected}
        </MultiSelectBox>
    </div>

}



